// Створення битви: вибір категорії + кількості питань.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, type Category } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { Loader } from '../components/Loader';
import { ErrorView } from '../components/ErrorView';
import { PageHeader } from '../components/PageHeader';
import { haptic } from '../lib/telegram';
import { IconTile, TabGlyph } from '../components/AppIcons';

export function BattleNew() {
  const navigate = useNavigate();
  const cats = useFetch<Category[]>(() => api.categories(), []);
  const [categoryId, setCategoryId] = useState<number | undefined>();
  const [count, setCount] = useState(10);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCreate() {
    setBusy(true);
    setError(null);
    haptic.medium();
    try {
      const res = await api.battles.create({ categoryId, questionCount: count });
      navigate(`/battles/${res.code}`, { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не вдалося створити битву');
      haptic.error();
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title="Нова битва" subtitle="Налаштуй параметри" backTo="/battles" />
      <div className="space-y-4 p-4">
        {/* Hero-банер: пояснюємо що таке битва — особливо для першого досвіду */}
        <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-tg-button/15 via-tg-secondary-bg to-tg-secondary-bg p-4">
          <div className="absolute -right-3 -top-3 opacity-10">
            <TabGlyph kind="battle" className="h-20 w-20" />
          </div>
          <div className="relative">
            <div className="mb-1 inline-flex items-center gap-1.5 rounded-full bg-tg-button/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-tg-button">
              <span>1 на 1 онлайн</span>
            </div>
            <h2 className="text-base font-bold leading-tight">Дуель на швидкість і знання</h2>
            <p className="mt-1 text-xs text-tg-hint leading-relaxed">
              Запроси друга — отримаєш персональне посилання. Граєте <strong className="text-tg-text">синхронно</strong>:
              хто більше правильних відповідей, той і виграв. Нічия — порівну.
            </p>
          </div>
        </section>

        {cats.loading && <Loader />}
        {cats.error && <ErrorView error={cats.error} onRetry={cats.reload} />}

        {cats.data && (
          <>
            <section className="card space-y-2">
              <label className="block text-xs font-medium text-tg-hint">Категорія</label>
              <select
                value={categoryId ?? ''}
                onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : undefined)}
                className="w-full rounded-lg bg-tg-bg px-3 py-2"
              >
                <option value="">Усі категорії (мікс)</option>
                {cats.data
                  .filter((c) => c.questionCount >= count)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nameUk} ({c.questionCount} пит.)
                    </option>
                  ))}
              </select>
            </section>

            <section className="card space-y-2">
              <label className="block text-xs font-medium text-tg-hint">Кількість питань</label>
              <div className="flex gap-2">
                {[5, 10, 15, 20].map((n) => (
                  <button
                    key={n}
                    onClick={() => setCount(n)}
                    className={`flex-1 rounded-lg py-2 text-sm font-medium ${
                      count === n ? 'bg-tg-button text-tg-button-text' : 'bg-tg-bg text-tg-text'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-tg-hint">
                {count === 5 && 'Швидкий бліц — 1–2 хвилини'}
                {count === 10 && 'Оптимально — 3–4 хвилини'}
                {count === 15 && 'Серйозна дуель — 5–6 хвилин'}
                {count === 20 && 'Повноцінний іспит-формат — 7–10 хвилин'}
              </p>
            </section>

            {error && (
              <div className="card border border-tg-destructive text-sm text-tg-destructive">
                {error}
              </div>
            )}

            <button onClick={handleCreate} disabled={busy} className="btn-primary w-full">
              {busy ? 'Створення…' : 'Створити битву'}
            </button>
            <p className="text-center text-[11px] text-tg-hint">
              Після створення отримаєш посилання — поділись ним у Telegram з другом.
            </p>

            {/* Як працює — короткий гайд для новачків */}
            <section className="card space-y-3">
              <h3 className="text-sm font-semibold">Як це працює?</h3>
              <Step n={1} title="Налаштуй битву" desc="Вибери категорію (наприклад, B) і скільки питань — 5, 10, 15 або 20." />
              <Step n={2} title="Поділись посиланням" desc="Натисни «Поділитись у Telegram» — друг отримає invite з кнопкою «Прийняти виклик»." />
              <Step n={3} title="Грайте синхронно" desc="Як тільки друг приєднається — старт. Ви бачите прогрес одне одного у реальному часі." />
              <Step n={4} title="Хто переможе?" desc="Більше правильних відповідей = перемога. Однаково — нічия. Результат запамʼятовується у статистиці." last />
            </section>

            {/* Фічі-плюшки */}
            <section className="card grid grid-cols-2 gap-3 text-xs">
              <Feat icon="flash" title="Реальний час" desc="Бачиш як друг просувається питаннями" />
              <Feat icon="target" title="Чесна гра" desc="У вас обох однаковий список питань" />
              <Feat icon="trophy" title="Рейтинг" desc="Перемоги йдуть у твою статистику" />
              <Feat icon="mistakes" title="Реванш" desc="Програв? Кинь виклик ще раз" />
            </section>
          </>
        )}
      </div>
    </>
  );
}

function Step({
  n,
  title,
  desc,
  last,
}: {
  n: number;
  title: string;
  desc: string;
  last?: boolean;
}) {
  return (
    <div className={`flex gap-3 ${last ? '' : 'border-b border-tg-bg/40 pb-3'}`}>
      <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-tg-button text-xs font-bold text-tg-button-text">
        {n}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium leading-tight">{title}</div>
        <div className="mt-0.5 text-xs text-tg-hint leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}

function Feat({
  icon,
  title,
  desc,
}: {
  icon: Parameters<typeof IconTile>[0]['kind'];
  title: string;
  desc: string;
}) {
  return (
    <div className="rounded-lg bg-tg-bg/40 p-2.5">
      <IconTile kind={icon} className="mb-1.5 h-7 w-7 rounded-lg" glyphClass="h-4 w-4" />
      <div className="text-xs font-semibold leading-tight">{title}</div>
      <div className="mt-0.5 text-[11px] text-tg-hint leading-snug">{desc}</div>
    </div>
  );
}
